import type { PropertyValueGroupV3, RawPropertyValueV3 } from '@cognite/sdk';

import type { PropertyRecord } from '../types';

/**
 * Type guard for SDK RawPropertyValueV3:
 * string | number | boolean | object | string[] | boolean[] | number[] | object[]
 */
export const isRawPropertyValueV3 = (
  value: unknown
): value is RawPropertyValueV3 => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return true;
  }

  if (value === null || value === undefined) {
    return false;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return true;
    }

    if (value.every((item) => typeof item === 'string')) {
      return true;
    }

    if (value.every((item) => typeof item === 'boolean')) {
      return true;
    }

    if (value.every((item) => typeof item === 'number')) {
      return true;
    }

    if (value.every((item) => typeof item === 'object' && item !== null)) {
      return true;
    }

    return false;
  }

  return typeof value === 'object';
};

export const isPropertyValueGroupV3 = (
  record: PropertyRecord
): record is PropertyValueGroupV3 =>
  Object.values(record).every(isRawPropertyValueV3);

/**
 * Validates that a PropertyRecord can be used as PropertyValueGroupV3 for instance upsert.
 * @throws Error when any property value is not a valid RawPropertyValueV3.
 */
export const assertPropertyValueGroupV3 = (
  record: PropertyRecord
): PropertyValueGroupV3 => {
  const result: PropertyValueGroupV3 = {};
  const invalidKeys: string[] = [];

  for (const [key, value] of Object.entries(record)) {
    if (!isRawPropertyValueV3(value)) {
      invalidKeys.push(key);
      continue;
    }
    result[key] = value;
  }

  if (invalidKeys.length > 0) {
    throw new Error(
      `Property record cannot be mapped to PropertyValueGroupV3. Invalid property keys: ${invalidKeys.join(', ')}`
    );
  }

  return result;
};
